<template>
  <div
    ref="container"
    role="region"
    :aria-label="$string('accessibility.carousel.aria_label')"
    class="product-carousel"
    :class="[
      `product-carousel--${settings.cardLayout}`,
      { [$class('loading')]: loading },
    ]"
    :style="{
      '--gap': settings.gap && `${settings.gap}px`
    }"
  >
    <div
      v-if="settings.successMessage"
      class="product-carousel__success"
    >
      <check-icon />

      <p
        class="text-setting-heading-desktop-3xs text-setting-heading-mobile-3xs"
        v-text="settings.successMessage"
      />
    </div>

    <h2
      v-if="settings.title"
      :id="`${settings.id || settings.template}-carousel-title`"
      class="product-carousel__title"
      :class="[
        settings.titleClass,
        `product-carousel__title--${settings.titleAlignment}`,
        {
          'has-success-message': settings.successMessage,
          'has-subtitle': settings.subtitle !== ''
        }
      ]"
    >
      <span v-html="settings.title" />
      <span
        v-if="settings.subtitle"
        class="product-carousel__subtitle text-body-2xs-regular"
        v-html="settings.subtitle"
      />
    </h2>

    <div class="product-carousel__container">
      <div
        ref="carousel"
        class="product-carousel__carousel"
        :class="{
          [`product-carousel__carousel--${settings.columnCount}`]: true,
          'product-carousel__carousel--centered': hasInsufficientSlides
            && settings.centerInsufficientSlides,
        }"
        :js-body-scroll-lock-ignore="true"
        @scroll="handleCarouselScroll"
      >
        <template
          v-for="(product, index) of products"
          :key="product.id"
        >
          <product-card
            v-if="product.id"
            ref="cards"
            :product="product"
            :card-layout="settings.cardLayout"
            :image-background-color="settings.cardBackground"
            :show-badges="settings.showBadges"
            :show-final-sale="settings.showFinalSale"
            :show-info="!settings.hideAllInfo"
            :padded-images="settings.paddedImages || false"
            :show-preorder-badge="settings.showPreorderBadge"
            :show-pricing="settings.showPricing"
            :show-quick-add="settings.showQuickAdd !== false"
            :show-secondary-name="settings.showSecondaryName"
            :card-content-alignment="settings.cardContentAlignment"
            :show-swatches="settings.showSwatches !== false"
            :show-wishlist="settings.showWishlist"
            :dy-strategy-id="dyStrategyId"
            :is-dickey-bundle="settings.isDickeyBundle"
            enable-secondary-image
            enable-variant-image
            show-updated-success
            is-recommendation
            :product-card-location="{
              key: getProductCardLocation()
            }"
            role="group"
            :aria-label="`slide ${index + 1} of ${getCarouselLength()}`"
            :aria-hidden="currentIndex > index || index >= currentIndex + numVisibleSlides"
            :tabindex="currentIndex <= index && index < currentIndex + numVisibleSlides ? 0 : -1"
          />
        </template>
        <div
          v-if="settings.showFinalImage && settings.finalImageDesktop"
          class="product-card product-cart__final-image"
          :class="settings.finalImageOverlay ? 'product-card__overlay-gradient' : ''"
        >
          <div class="product-card__image-container">
            <responsive-image
              v-if="!isMobile"
              class="product-card__primary-image"
              :image="getImage(settings.finalImageDesktop )"
              :lazy-load="false"
              ratio="3:4"
              :sizes="imageSizes"
            />
            <responsive-image
              v-if="isMobile"
              class="product-card__primary-image"
              :image="getImage(settings.finalImageMobile)"
              :lazy-load="false"
              ratio="3:4"
              :sizes="imageSizes"
            />
            <p
              v-if="settings.finalImageText && !settings.finalImageUrl"
              class="product-card__image-overlay-text"
              v-text="settings.finalImageText"
            />

            <btn
              v-if="settings.finalImageUrl"
              :url="settings.finalImageUrl"
              class="product-card__image-overlay"
              :title="settings.finalImageText || ''"
              modifiers="text inline"
              :label="settings.finalImageText || false"
            />
          </div>
        </div>
      </div>

      <template v-if="showArrows">
        <button
          ref="prev"
          class="button product-carousel__btn product-carousel__btn--prev"
          disabled
          type="button"
          :aria-label="$string('accessibility.carousel.previous')"
          @click="moveProductCarousel('prev')"
        >
          <arrow-left />
        </button>

        <button
          ref="next"
          class="button product-carousel__btn product-carousel__btn--next"
          type="button"
          :aria-label="$string('accessibility.carousel.next')"
          @click="moveProductCarousel('next')"
        >
          <arrow-right />
        </button>
      </template>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Product carousel (product-carousel)
 * -----------------------------------------------------------------------------
 * Reusable carousel settings containing product cards.
 *
 * @param {Boolean} [loading] - Loading state.
 * @param {Object} settings - Object containing carousel settings.
 * @param {Object} settings.cardBackground - Background color for product
 * cards in carousel.
 * @param {Object} settings.cardLayout - Size of product card to be displayed,
 * accepts values 'small', 'regular', 'large' or 'center'.
 * @param {Object} settings.columnCount - Number of columns in carousel,
 * accepts values 'small' for 6 columns and 'medium' for 4 columns.
 * @param {Object} settings.titleAlignment - Text alignment for carousel title,
 * accepts values 'default' and 'center'.
 * @param {Array} products - Products to be displayed in carousel.
 * @param {Array} dyStrategyId - A Dynamic Yield strategy ID for tracking.
 * @param {String} intent - Identifies type of recommendation,
 * accepts values 'complementary' and 'related'.
 *
 */
import { mapState } from 'vuex'
import ProductCard from '~global/product-card/product-card'
import ResponsiveImage from '~global/images/responsive-image'
import Btn from '~global/btn/btn'
import ArrowLeft from '~icons/directional-navigation/arrow/left-large.svg'
import ArrowRight from '~icons/directional-navigation/arrow/right-large.svg'
import CheckIcon from '~icons/general/check.svg'

export default {
  name: 'ProductCarousel',
  components: {
    ProductCard,
    ResponsiveImage,
    Btn,
    ArrowLeft,
    ArrowRight,
    CheckIcon,
  },

  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    settings: {
      type: Object,
      default: () => ({
        id: '',
        cardBackground: '--color-brand-white',
        cardLayout: 'regular',
        columnCount: 'small',
        paddedImages: false,
        showBadges: false,
        showCarouselArrows: true,
        showFinalSale: false,
        showPreorderBadge: false,
        showPricing: true,
        showQuickAdd: true,
        showSecondaryName: false,
        showSwatches: true,
        showWishlist: false,
        subtitle: '',
        title: '',
        titleAlignment: 'default',
        cardContentAlignment: '',
        titleClass: '',
      }),
    },
    products: {
      type: Array,
      default: () => [],
    },
    dyStrategyId: {
      type: Number,
      default: null,
    },
    customReport: {
      type: Boolean,
      default: false,
    },
    intent: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      cnvsGlobal: true,
      currentIndex: 0,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute image sizes.
     */
    imageSizes() {
      return this.settings.cardLayout === 'regular'
        ? '(min-width: 1024px) 25vw, 100vw'
        : '(min-width: 1024px) 25vw, 50vw'
    },

    numVisibleSlides() {
      if (this.isMobile) {
        return 2
      }

      if (this.settings?.columnCount === 'medium') {
        return 4
      }

      if (this.settings?.columnCount === 'small') {
        return 6
      }

      return this.settings?.columnCount
    },

    /**
     * If there are insufficient slides to cause overflow
     */
    hasInsufficientSlides() {
      return this.getCarouselLength() < this.numVisibleSlides
    },

    /**
     * Handle carousel arrow display
     * @returns {Boolean}
     */
    showArrows() {
      if (!this.settings.showCarouselArrows) {
        return false
      }

      return this.getCarouselLength() > this.numVisibleSlides
    },
  },

  mounted() {
    this.handleCarouselScroll()
    this.getCarouselFooterHeight()
    this.setCarouselResizeObserver()
  },

  methods: {

    getImage(image) {
      if (image.src) {
        return image
      }
      return {
        src: image,
      }
    },

    /**
     * Handle scrolling product carousel.
     * @param {String} direction - Direction to move carousel of sign post.
     */
    moveProductCarousel(direction) {
      const carousel = this.$refs.carousel
      const cards = this.$refs.cards

      if (!carousel || !cards) {
        return
      }

      const card = cards[0].$el
      const cardWidth = card.clientWidth

      this.currentIndex = direction === 'prev'
        ? Math.max(0, this.currentIndex - 1)
        : Math.min(cards.length, this.currentIndex + 1)

      carousel.scrollLeft = this.currentIndex * cardWidth
    },

    /**
     * Handles carousel scroll event to update arrows
     */
    handleCarouselScroll() {
      const carousel = this.$refs.carousel

      if (!carousel) {
        return
      }

      const scrollWidth = carousel.scrollWidth
      const scroll = carousel.scrollLeft
      const carouselWidth = carousel.clientWidth

      const prevButton = this.$refs.prev
      const nextButton = this.$refs.next

      const hideButton = (button) => {
        button?.setAttribute('disabled', true)
      }

      const showButton = (button) => {
        button?.removeAttribute('disabled')
      }

      if (scroll === 0) {
        hideButton(prevButton)
      } else {
        showButton(prevButton)
      }

      if ((scroll + carouselWidth) >= scrollWidth) {
        hideButton(nextButton)
      } else {
        showButton(nextButton)
      }
    },

    /**
     * Resize observer on carousel
     */
    setCarouselResizeObserver() {
      const carousel = this.$refs.carousel

      if (!carousel) {
        return
      }

      const self = this
      const resizeObserver = new ResizeObserver((entries) => {
        entries.forEach(() => {
          self.getCarouselFooterHeight()
        })
      })

      resizeObserver.observe(carousel)
    },

    /**
     * Compute carousel footer height to offset carousel arrows
     * @returns {Number}
     */
    getCarouselFooterHeight() {
      const container = this.$refs.container
      const cards = this.$refs.cards

      if (!container || !cards) {
        return
      }

      const card = cards[0].$el
      const cardHeight = card?.getBoundingClientRect().height || 0

      const cardImage = card.querySelector('.product-card__image-container')
      const cardImageHeight = cardImage?.getBoundingClientRect().height || 0

      const footerHeight = cardHeight - cardImageHeight

      container.style.setProperty('--footer-height', `${footerHeight}px`)
    },

    /**
     * Adds to carousel length if final image is enabled and present.
     * @returns {Number} - Length of carousel.
     */
    getCarouselLength() {
      let carouselLength = this.products?.length

      if (this.settings.showFinalImage && this.settings.finalImageDesktop) {
        carouselLength += 1
      }

      return carouselLength
    },

    /**
    * Returns location of product carousel
    * @returns {String} - Location of carousel
    */
    getProductCardLocation() {
      if (this.customReport) {
        if (this.intent === 'related') {
          return 'productTrayShopifyRecommendationsRelated'
        }

        if (this.intent === 'complementary') {
          return 'productTrayShopifyRecommendationsComplemetary'
        }
        return 'productTray'
      }
      return 'carousel'
    },
  },
}
</script>
